import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Pencil } from 'lucide-react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  canActivateCustomer,
  canDeactivateCustomer,
} from '#/modules/crmMgmt/customer/helper/customerStatus';
import useCustomerStatusMutation, {
  CUSTOMER_ACTIVATE_CONFIRM,
  CUSTOMER_DEACTIVATE_CONFIRM,
} from '#/modules/crmMgmt/customer/hooks/useCustomerStatusMutation';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';

const CustomerCommandBar = ({ detail, onRefetch }) => {
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canUpdate = useCan(PERM.CUSTOMER_UPDATE);
  const canActivatePerm = useCan(PERM.CUSTOMER_ACTIVATE);

  const id = detail?.id;
  const status = detail?.status;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;

  const statusMutation = useCustomerStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onAfterSuccess: async () => {
      await onRefetch?.();
    },
    onVersionConflict: () => {
      onRefetch?.();
    },
  });

  const isPending = statusMutation.isPending;
  const canActivate = canActivatePerm && canActivateCustomer(status);
  const canDeactivate = canActivatePerm && canDeactivateCustomer(status);

  async function handleActivate() {
    const ok = await confirm(CUSTOMER_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(CUSTOMER_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  return (
    <>
      {ConfirmDialogHost}
      {canUpdate && id ? (
        <Button
          asChild
          variant="default"
          className={cn('document-detail-edit-action')}
        >
          <Link to={ROUTES.customerEdit(id)} aria-label="編輯">
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canActivate ? (
        <Button disabled={isPending} onClick={handleActivate}>
          啟用
        </Button>
      ) : null}
      {canDeactivate ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleDeactivate}
        >
          停用
        </Button>
      ) : null}
    </>
  );
};

CustomerCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default CustomerCommandBar;
